/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';

import { SecurityHelper } from 'sarsaparilla';

import type { RecaptchaRegion } from '../../recaptcha/recaptcha.types';

import {
    getFingerprint,
    getConversationId,
    storeTemporaryToken,
    getTemporaryToken,
    removeTemporaryToken,
    storeAccountRegistrationPayload,
    storeEmailConfirmedToken,
} from './account.utils';

import type {
    GetAccountStatusResponse,
    PostAccountRegistrationResponse,
    PostApplyNewPasswordResponse,
    PostGuestLoginResponse,
    PostLoginResponse,
    PostMfaTokenResponse,
    PostRequestPasswordResetResponse,
    PostSetPasswordResponse,
    PutAccountEmailValidationResponse,
} from './account.types';

// ---------------------------------------------------------------------------------------

/**
 * Get the status of the account service. Used to disable login and display an announcement
 * about the service being down.
 */
export function getAccountStatus({ signal }: { signal?: AbortSignal }) {
    return axios
        .get<GetAccountStatusResponse>('/api/accounts/status', { signal })
        .then((response) => {
            return response.data;
        });
}

// ---------------------------------------------------------------------------------------

/**
 * Log in with a username and password
 */
export async function postLoginV2({
    username,
    password,
    token,
    recaptchaToken,
    recaptchaType,
}: {
    username: string;
    password: string;
    token?: string; // newly created account will have this token
    recaptchaToken?: string | null;
    recaptchaType?: string;
}) {
    const fingerprint = await getFingerprint();
    const userAgent = navigator.userAgent;

    const payload = {
        username,
        password,
        fingerprint,
        userAgent,
        session_type: SecurityHelper.isWidgetSession() ? 'widget' : undefined,
        token,
        recaptchaToken,
        recaptchaType,
        conversation_id: getConversationId() ?? undefined,
    };

    return axios
        .post<PostLoginResponse>('/api/accounts/login/v2/', payload)
        .then((response) => {
            // If the user has MFA enabled we save the token from this API call response
            // to use in the header of the MFA POST call
            if (response?.data?.mfa_login && response?.data?.access_token) {
                storeTemporaryToken(response.data.access_token);
            }

            return response;
        });
}

/**
 * Log in as a guest
 */
export async function postGuestLogin() {
    return axios.post<PostGuestLoginResponse>('/api/accounts/guest/login', {});
}

/**
 * Validate a user-provided MFA code
 */
export async function postMfaToken(token: string) {
    const fingerprint = await getFingerprint();
    const userAgent = navigator.userAgent;
    const accessToken = getTemporaryToken();

    if (!accessToken) {
        throw new Error('No temporary access token found');
    }

    const payload = {
        token,
        fingerprint,
        userAgent,
        type: 'login', // unclear if this is required or can change
        session_type: SecurityHelper.isWidgetSession() ? 'widget' : undefined,
    };

    return axios
        .post<PostMfaTokenResponse>('/api/accounts/login/v2/mfa', payload, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
        .then((response) => {
            removeTemporaryToken();
            return response;
        });
}

/**
 * Enable or disable the MFA prompt
 */
export function patchMfaPrompt(show: boolean = false) {
    return axios.patch('/api/accounts/account/mfa/prompt', null, {
        headers: {
            Authorization: SecurityHelper.getAuthHeader(),
        },
        params: {
            show,
        },
    });
}

// ---------------------------------------------------------------------------------------

/**
 * Kick off the password reset process
 */
export function postRequestPasswordReset({
    email,
    recaptchaToken,
    recaptchaRegion,
}: {
    email: string;
    recaptchaToken: string;
    recaptchaRegion: RecaptchaRegion;
}) {
    return axios
        .post<PostRequestPasswordResetResponse>('/api/accounts/passwordreset', {
            email,
            recaptcha_token: recaptchaToken,
            recaptcha_type: recaptchaRegion,
        })
        .then((response) => {
            if (!response?.data?.success) {
                throw new Error('Failed to send reset link');
            }

            return response;
        });
}

/**
 * Password reset step 2: Validate the emailed verification code and save the new password
 */
export function postApplyNewPassword({
    email,
    token,
    password,
}: {
    email?: string; // optional for magic link flow
    token: string;
    password: string;
}) {
    return axios
        .post<PostApplyNewPasswordResponse>(`/api/accounts/passwordreset/${token}`, {
            email,
            token,
            password,
        })
        .then((response) => {
            // If the user has MFA enabled we save the token from this API call response
            // to use in the header of the MFA POST call
            if (response?.data?.mfa_login && response?.data?.access_token) {
                storeTemporaryToken(response.data.access_token);
            }

            return response;
        });
}

// ---------------------------------------------------------------------------------------

/**
 * Create a new account, step 1: Get name, email, phone
 * (also resend email)
 */
export async function postAccountRegistration({
    email,
    firstName,
    lastName,
    cellPhone,
    optIn,
    recaptchaToken,
    recaptchaRegion,
}: {
    email: string;
    firstName: string;
    lastName: string;
    cellPhone: string;
    optIn: boolean;
    recaptchaToken: string;
    recaptchaRegion: RecaptchaRegion;
}) {
    // Simulate an Axios error with message "additional challenge required"
    // Use this to check v2 fallback
    // throw {
    //     response: {
    //         data: {
    //             error: 'additional challenge required',
    //         },
    //     },
    // };

    const payload = {
        email,
        first_name: firstName,
        last_name: lastName,
        cell_phone: cellPhone,
        opt_in: optIn,
        system: {
            section: 'initializeRegistration',
            code: recaptchaToken,
            region: recaptchaRegion,
        },
    };

    storeAccountRegistrationPayload({ email, firstName, lastName, cellPhone, optIn });

    return axios.post<PostAccountRegistrationResponse>(
        '/api/accounts/registration',
        payload
    );
}

/**
 * Create a new account, step 2: Validate the email address
 */
export async function putAccountEmailValidation(code: string) {
    // silly API wants a space between the first 3 digits and last 3 digits
    const formattedCode =
        code.slice(0, 3) + (code.length > 3 ? ' ' : '') + code.slice(3, 6);

    return axios
        .put<PutAccountEmailValidationResponse>('/api/accounts/registration/validate', {
            code: formattedCode,
        })
        .then((response) => {
            if (response?.data?.token) {
                storeEmailConfirmedToken(response.data.token);
            }

            return response;
        });
}

/**
 * Create a new account, step 3: Save user's password
 */
export async function postSetPassword({
    token,
    password,
}: {
    token: string;
    password: string;
}) {
    const userAgent = navigator.userAgent;

    return axios.post<PostSetPasswordResponse>(`/api/accounts/registration/${token}`, {
        token,
        password,
        userAgent,
        conversation_id: getConversationId() ?? undefined,
    });
}

// cSpell:ignore passwordreset, fingerprintjs
